const initScrollAnimations = (): void => {
  const targets = document.querySelectorAll<HTMLElement>('[data-animate]')
  if (!targets.length) return

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        const el = entry.target as HTMLElement
        const delay = parseInt(el.dataset.animateDelay || '0', 10)
        setTimeout(() => {
          el.classList.add('is-visible')
        }, delay)
        observer.unobserve(el)
      })
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  )

  targets.forEach((el) => observer.observe(el))
}

const initScrollFade = (): void => {
  const els = document.querySelectorAll<HTMLElement>('[data-scroll-fade]')
  if (!els.length) return

  const update = (): void => {
    const vh = window.innerHeight
    els.forEach((el) => {
      const rect = el.getBoundingClientRect()
      const start = vh * 1.1
      const end = vh * 0.25
      const progress = Math.min(Math.max((start - rect.top) / (start - end), 0), 1)
      el.style.opacity = String(progress)
      el.style.transform = `translateY(${(1 - progress) * 50}px)`
    })
  }

  let ticking = false
  const onScroll = (): void => {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      update()
      ticking = false
    })
  }

  window.addEventListener('scroll', onScroll, { passive: true })
  update()
}

const initFlowLines = (): void => {
  const lines = document.querySelectorAll<HTMLElement>('[data-flow-line]')
  if (!lines.length) return

  const update = (): void => {
    const vh = window.innerHeight
    const trigger = vh * 0.55
    lines.forEach((line) => {
      const lineH = line.offsetHeight
      if (lineH <= 0) return
      const top = line.getBoundingClientRect().top
      const scrolled = trigger - top
      const ratio = Math.min(Math.max(scrolled / lineH, 0), 1)
      line.style.transform = `scaleY(${ratio})`
    })
  }

  let ticking = false
  const onScroll = (): void => {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      update()
      ticking = false
    })
  }

  window.addEventListener('scroll', onScroll, { passive: true })
  update()
}

const initPageTop = (): void => {
  const btns = [
    document.getElementById('pageTopBtn'),
    document.getElementById('pageTopBtnPc'),
  ]

  const handleClick = (): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }

  btns.forEach((btn) => {
    if (!btn) return
    btn.addEventListener('click', handleClick)
    btn.addEventListener('keydown', handleKeydown)
  })
}

const initHamburgerMenu = (): void => {
  const hamburgerBtn = document.getElementById('hamburgerBtn')
  const mobileMenu = document.getElementById('mobileMenu')
  const mobileMenuClose = document.getElementById('mobileMenuClose')
  if (!hamburgerBtn || !mobileMenu || !mobileMenuClose) return

  const TRANSITION_TIMEOUT = 500

  const menuItems =
    mobileMenu.querySelectorAll<HTMLElement>('[data-menu-item]')

  const open = (): void => {
    mobileMenu.classList.remove('hidden')
    requestAnimationFrame(() => {
      mobileMenu.classList.add('is-open')
      menuItems.forEach((item, i) => {
        setTimeout(() => item.classList.add('is-visible'), i * 80)
      })
    })
    document.body.style.overflow = 'hidden'
    hamburgerBtn.setAttribute('aria-expanded', 'true')
  }

  const close = (): void => {
    mobileMenu.classList.remove('is-open')
    menuItems.forEach((item) => item.classList.remove('is-visible'))

    const fallbackTimer = setTimeout(() => {
      mobileMenu.classList.add('hidden')
    }, TRANSITION_TIMEOUT)

    mobileMenu.addEventListener(
      'transitionend',
      () => {
        clearTimeout(fallbackTimer)
        mobileMenu.classList.add('hidden')
      },
      { once: true }
    )

    document.body.style.overflow = ''
    hamburgerBtn.setAttribute('aria-expanded', 'false')
  }

  hamburgerBtn.addEventListener('click', open)
  mobileMenuClose.addEventListener('click', close)

  // メニュー内のリンクをクリックしたら閉じる
  mobileMenu.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', close)
  })

  // ESCキーで閉じる
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape' && !mobileMenu.classList.contains('hidden')) {
      close()
      hamburgerBtn.focus()
    }
  })

  // PC幅に切り替わったときモバイルメニューが開いていれば閉じる
  const mql = window.matchMedia('(min-width: 1024px)')
  mql.addEventListener('change', (e) => {
    if (e.matches && !mobileMenu.classList.contains('hidden')) {
      mobileMenu.classList.remove('is-open')
      menuItems.forEach((item) => item.classList.remove('is-visible'))
      mobileMenu.classList.add('hidden')
      document.body.style.overflow = ''
      hamburgerBtn.setAttribute('aria-expanded', 'false')
    }
  })
}

const initMobileAccordion = (): void => {
  const btn = document.getElementById('mobileAccordionBtn')
  const content = document.getElementById('mobileAccordionContent')
  const arrow = document.getElementById('mobileAccordionArrow')
  if (!btn || !content || !arrow) return

  // aria-controls を動的に設定
  btn.setAttribute('aria-controls', 'mobileAccordionContent')

  const links = content.querySelectorAll<HTMLElement>('a')

  btn.addEventListener('click', () => {
    const isOpen = !content.classList.contains('hidden')
    if (isOpen) {
      links.forEach((link) => link.classList.remove('is-visible'))
      content.classList.add('hidden')
      content.classList.remove('flex')
      arrow.style.transform = ''
      btn.setAttribute('aria-expanded', 'false')
    } else {
      content.classList.remove('hidden')
      content.classList.add('flex')
      arrow.style.transform = 'rotate(180deg)'
      btn.setAttribute('aria-expanded', 'true')
      links.forEach((link, i) => {
        setTimeout(() => link.classList.add('is-visible'), i * 60)
      })
    }
  })
}

const initHeroIntro = (): void => {
  const section = document.querySelector('.section-hero')
  if (!section) return

  const photos = section.querySelectorAll<HTMLElement>('[data-hero-anim="photo"]')
  const catches = section.querySelectorAll<HTMLElement>('[data-hero-anim="catch"]')
  const phils = section.querySelectorAll<HTMLElement>(
    '[data-hero-anim="phil"], [data-hero-anim="phil-sp"]'
  )

  if (!photos.length && !catches.length && !phils.length) return

  const revealAll = (els: NodeListOf<HTMLElement>): void => {
    els.forEach((el) => el.classList.add('is-revealed'))
  }

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    revealAll(photos)
    revealAll(catches)
    revealAll(phils)
    return
  }

  window.setTimeout(() => revealAll(catches), 150)
  window.setTimeout(() => revealAll(photos), 900)
  window.setTimeout(() => revealAll(phils), 2000)
}

const initPcDropdown = (): void => {
  const toggle = document.getElementById('pcMorikenToggle')
  const dropdown = document.getElementById('pcMorikenDropdown')
  const arrow = document.getElementById('pcMorikenArrow')
  const navItem = document.getElementById('pcNavMoriken')
  if (!toggle || !dropdown || !arrow || !navItem) return

  let closeTimer: ReturnType<typeof setTimeout> | null = null

  const open = (): void => {
    if (closeTimer) {
      clearTimeout(closeTimer)
      closeTimer = null
    }
    dropdown.classList.remove('hidden')
    dropdown.classList.add('flex', 'flex-col')
    arrow.style.transform = 'rotate(180deg)'
    toggle.setAttribute('aria-expanded', 'true')
  }

  const close = (): void => {
    dropdown.classList.add('hidden')
    dropdown.classList.remove('flex', 'flex-col')
    arrow.style.transform = ''
    toggle.setAttribute('aria-expanded', 'false')
  }

  const delayedClose = (): void => {
    closeTimer = setTimeout(close, 200)
  }

  navItem.addEventListener('mouseenter', open)
  navItem.addEventListener('mouseleave', delayedClose)

  // キーボード: click イベントで toggle（Enter / Space キー対応）
  toggle.addEventListener('click', () => {
    const isOpen = toggle.getAttribute('aria-expanded') === 'true'
    if (isOpen) {
      close()
    } else {
      open()
    }
  })

  // ドロップダウン内で Escape キーが押されたら close してトグルにフォーカス
  dropdown.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      close()
      toggle.focus()
    }
  })

  // ドロップダウン内の最後のリンクから Tab で抜けたら close
  const dropdownLinks = dropdown.querySelectorAll<HTMLAnchorElement>('a')
  const lastLink = dropdownLinks[dropdownLinks.length - 1]
  if (lastLink) {
    lastLink.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Tab' && !e.shiftKey) {
        close()
      }
    })
  }
}

document.addEventListener('DOMContentLoaded', () => {
  initHeroIntro()
  initScrollAnimations()
  initScrollFade()
  initFlowLines()
  initPageTop()
  initHamburgerMenu()
  initMobileAccordion()
  initPcDropdown()
})
